import { VOLATILE_ATTRS, type DiffMark } from '../schema';

/**
 * Converts an arbitrary value into a deterministic string representation.
 *
 * Why:
 * - JS object key order must not affect equality
 * - Attrs can contain nested objects / arrays
 * - We want a single comparison rule across the entire engine
 */
export const stableValue = (value: unknown): string => {
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return `[${value.map(stableValue).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([key, entryValue]) => `${key}:${stableValue(entryValue)}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

// prosemirror-model rewrites style text (`text-align:center` -> `text-align: center;`), so compare parsed declarations, not the raw string.
const parseStyleDeclarations = (style: string): Record<string, string> => {
  const declarations: Record<string, string> = {};
  for (const part of style.split(';')) {
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1) continue;
    const property = part.slice(0, colonIndex).trim().toLowerCase();
    const value = part
      .slice(colonIndex + 1)
      .trim()
      .replace(/\s+/g, ' ');
    if (property) declarations[property] = value;
  }
  return declarations;
};

/** Replaces a string `style` attr with its parsed declaration map so stableValue compares it by meaning. */
export const normalizeStyleAttr = (attrs: Record<string, unknown>): Record<string, unknown> =>
  typeof attrs.style === 'string' ? { ...attrs, style: parseStyleDeclarations(attrs.style) } : attrs;

/**
 * Strips volatile attributes that are derived or runtime-only and should
 * not by themselves produce a diff (e.g. heading numbering).
 */
export const normalizeAttrs = (nodeType: string, attrs: Record<string, unknown>): Record<string, unknown> => {
  const ignored = new Set(VOLATILE_ATTRS[nodeType] ?? []);
  return normalizeStyleAttr(Object.fromEntries(Object.entries(attrs).filter(([key]) => !ignored.has(key))));
};

/** Shared attribute comparison used by all structural diff algorithms. */
export const attrsEqual = (
  nodeType: string,
  currAttrs: Record<string, unknown>,
  prevAttrs: Record<string, unknown>
): boolean => stableValue(normalizeAttrs(nodeType, currAttrs)) === stableValue(normalizeAttrs(nodeType, prevAttrs));

/**
 * Order-independent mark equality.
 *
 * Marks are sorted by name before comparison so that semantically identical
 * mark sets like `[bold, italic]` and `[italic, bold]` are treated as equal.
 * This prevents false format-change diffs when the DOM nesting order differs
 * (e.g. `<strong><em>` vs `<em><strong>`).
 */
export const marksEqual = (a: readonly DiffMark[], b: readonly DiffMark[]): boolean => {
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;
  const sortKey = (m: DiffMark) => `${m.name}:${stableValue(m.attrs)}`;
  const sortedA = [...a].sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  const sortedB = [...b].sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  return sortedA.every((mark, i) => sortKey(mark) === sortKey(sortedB[i]));
};
